// Angular
import { inject } from '@angular/core';
import { ResolveFn, Router } from '@angular/router';

// NGRX
import { Store, select } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { map, take, filter, switchMap } from 'rxjs/operators';

// OLCI
import * as fromRoot from '../reducers';
import * as olci from '../actions/olci';
import { CONSTANTS } from '../constants';
import { ReservationRetrievalObject } from '../models';
import * as guestActions from '../actions/guest.actions';
import { GuestService } from '../modules/core/services/guest.service';
import { GatingServices } from '../modules/core/services/gating.services';
import { IRetrieveCheckinStatusRequestPayload } from '../models/action-payloads/retrieve-checkin-status-payload';

export const guestsResolver: ResolveFn<any> = (): Observable<boolean> => {
  const guestsService: GuestService = inject(GuestService);
  const gatingService: GatingServices = inject(GatingServices);
	const store: Store<fromRoot.AppState> = inject(Store<fromRoot.AppState>);
  const router: Router = inject(Router);
  const constants: any = inject(CONSTANTS);
  let reservationData: ReservationRetrievalObject;

  return store.pipe(
    select(fromRoot.getSessionState),
    filter(
      (resData) =>
        resData.reservationRetrievalResp !== null || !!resData.error
    ),
    take(1),
    switchMap((resData: any) => {
      if (resData.error) {
        router.navigate([constants.ROUTES.ERROR]);
        return of(false);
      }

      if (!guestsService.isInTheStore) {
        reservationData = gatingService.getReservationData();
        if (reservationData) {
          const payload = {
            vdsId: reservationData.vdsId,
            brand: reservationData.brand,
            shipCode: reservationData.shipCode,
            sailDate: reservationData.sailDate,
          };

          guestsService
            .getGuestsProfileBooking(payload)
            .pipe(take(1))
            .subscribe((res) => onDispatchGuestsProfileBooking(res, constants, store, guestsService));
        } else {
          (<any>window).location.href =
            gatingService.getGAAccountURL();
          return of(false);
        }
      }

      return store.pipe(
        select(fromRoot.getGuestState),
        filter((guestsState) => validateGuestsNoErrors(guestsState)),
        take(1),
        map((guestsState) => onDispatchNavigateToError(guestsState, store))
      );
    })
  );
}

const validateGuestsNoErrors = (guestsState: any) => {
  return guestsState.guests.length > 0 || !!guestsState.error;
}

const onDispatchGuestsProfileBooking = (r: any, constants: any, store: Store<fromRoot.AppState>, guestsService: GuestService) => {
  if (r?.status !== constants.HTTP_STATUS_CODE.BACKEND_ERROR) {
    onDispatchPercentageCompletion(r, store);
    store.dispatch(
      new guestActions.RetrieveGuestSecurityPhotoListAction(
        guestsService.createSecurityPhotoPayload(r.payload)
      )
    );
  }
  store.dispatch(
    new guestActions.LoadGuestsProfileBookingMicroserviceAction(r)
  );
}

const onDispatchPercentageCompletion = (r: any, store: Store<fromRoot.AppState>) => {
  const checkinStatusPayload: IRetrieveCheckinStatusRequestPayload = {
    guestReservationIds: r?.payload.map((guest: any) => ({
      bookingId: guest.bookingId,
      guestId: guest.guestId,
    })),
  };
  store.dispatch(
    new guestActions.GetPercentageCompletionAction(checkinStatusPayload)
  );
}

const onDispatchNavigateToError = (guestsState: any, store: Store<fromRoot.AppState>) => {
  if (guestsState.error) {
    store.dispatch(new olci.NavigateToError());
    return false;
  }
  return true;
}
